import { useMutation } from "@apollo/client";
import Button from "components/base/Button";
import { FileSelector, Input, Text } from "components/base/Form";
import { Alert } from "components/base/Toast";
import { useState } from "react";
import { InlineSpinner } from "components/base/Spinner";
import { CREATE_VIDEO, CREATE_VIDEO_SOURCE } from "./graphql";

const CreateVideoForm = ({ onClose }) => {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [videoFile, setVideoFile] = useState(null);
  const [createdVideoId, setCreatedVideoId] = useState(null);

  const [createVideo, createVideoRes] = useMutation(CREATE_VIDEO, {
    onCompleted(data) {
      const videoId = data.createVideo.video.id;
      setCreatedVideoId(videoId);

      // 如果有选择文件，自动上传
      if (videoFile) {
        createVideoSource({ variables: { videoId, file: videoFile } });
      } else {
        Alert("success", "Video created successfully!");
        onClose();
      }
    },
    onError(error) {
      Alert("error", error.message);
    },
    refetchQueries: ["FETCH_ALL_VIDEOS"],
    awaitRefetchQueries: true,
  });

  const [createVideoSource, createVideoSourceRes] = useMutation(
    CREATE_VIDEO_SOURCE,
    {
      onCompleted() {
        Alert("success", "Video created and uploaded successfully!");
        onClose();
      },
      onError(error) {
        Alert("error", error.message);
      },
      refetchQueries: ["FETCH_ALL_VIDEOS"],
      awaitRefetchQueries: true,
    },
  );

  const handleSubmit = () => {
    if (!name.trim()) {
      Alert("error", "Please enter a video name");
      return;
    }
    createVideo({ variables: { name, description } });
  };

  const isLoading = createVideoRes.loading || createVideoSourceRes.loading;

  return (
    <div className="px-10 pb-10 space-y-6 relative">
      {/* Uploading Overlay */}
      {isLoading && (
        <div className="fixed inset-0 z-50 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 flex flex-col items-center space-y-4">
            <InlineSpinner size={40} />
            <p className="text-sm font-medium text-gray-900 dark:text-white">
              {createVideoRes.loading && "Creating video..."}
              {createVideoSourceRes.loading && "Uploading video file..."}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Please do not close this window
            </p>
          </div>
        </div>
      )}

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-2">
            Video Name <span className="text-red-500">*</span>
          </label>
          <Input
            placeholder="Enter video name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={isLoading}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">Description</label>
          <Text
            placeholder="Enter video description (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            disabled={isLoading}
          />
        </div>

        <div>
          <label className="block text-sm font-medium mb-2">
            Video File {videoFile ? "✓" : "(optional)"}
          </label>
          <FileSelector
            title={
              videoFile ? `Selected: ${videoFile.name}` : "Select video file"
            }
            accept="video/*"
            disabled={isLoading}
            onChange={(e) => {
              if (e.target.files && e.target.files[0]) {
                setVideoFile(e.target.files[0]);
              }
            }}
          />
          {videoFile && (
            <div className="mt-2 text-sm text-gray-500">
              <span className="font-medium">{videoFile.name}</span>
              <span className="ml-2">
                ({(videoFile.size / 1024 / 1024).toFixed(2)} MB)
              </span>
              <button
                className="ml-3 text-red-500 hover:text-red-700"
                onClick={() => setVideoFile(null)}
                disabled={isLoading}
              >
                Remove
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="flex space-x-3 pt-4 border-t dark:border-gray-700">
        <Button
          primary
          loading={isLoading}
          disabled={isLoading}
          onClick={handleSubmit}
        >
          {videoFile ? "Create & Upload" : "Create Video"}
        </Button>
        <Button disabled={isLoading} onClick={onClose}>
          Cancel
        </Button>
      </div>
    </div>
  );
};

export default CreateVideoForm;
